import React, { useCallback, useMemo } from "react";
import { Lock } from "@mui/icons-material";
import { Box } from "@mui/material";

import { selectReportsByVersionId, useGetAccountReports, useGetVersionStatusQuery } from "fond/api";
import ArchitectureModal from "fond/architecture/Architecture";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { inputLayerGroups, LayerGroupIds } from "fond/layers";
import { useGetPlannerLayers } from "fond/project/panels/ExtendedDesign/hooks/useGetPlannerLayers";
import { usePlannerDataPrepStatus } from "fond/project/panels/ExtendedDesign/hooks/usePlannerDataPrepStatus";
import { LayerDrawAction, LayerPathPreferencesAction, LayerUploadAction } from "fond/project/panels/InputPanel/actions";
import LayerPanel from "fond/project/panels/InputPanel/LayerPanel";
import MultiLayerUploadForm from "fond/project/panels/InputPanel/MultiLayerUploadForm";
import { closeArchitectureModal, Modals, openArchitectureModal, StatusTypes } from "fond/project/redux";
import { SolvePanel } from "fond/project/solve";
import BlockersRefinementPanel from "fond/project/solve/refinements/BlockersRefinementPanel";
import DropAssignmentsRefinementPanel from "fond/project/solve/refinements/DropAssignmentsRefinementPanel";
import HubsRefinementPanel from "fond/project/solve/refinements/HubsRefinementPanel";
import { Architecture, LayerIds, Project, WORKFLOW_KEYS } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { Actions, permissionCheck } from "fond/utils/permissions";
import { AccordionStepper, AccordionStepperPane, NonIdealState, StackedNavigation } from "fond/widgets";

import ArchitecturePanel from "../ArchitecturePanel";
import AreaSelect from "../AreaSelectPanel";
import Draw from "../DrawPanel";
import ImportData from "../ImportDataPanel";
import InputMethodsPanel from "../InputMethodsPanel";
import ConstructionPrintExport from "../MuPower/ConstructionPrintExport";
import PolygonSelect from "../PolygonSelectPanel";
import RemoveData from "../RemoveDataPanel";
import ReportPanel from "../ReportPanel/ReportPanel";
import UploadPanel from "../UploadPanel";

import BlockersDraw from "./Blockers/BlockersDraw";
import DataPrepPanel from "./DataPrep/DataPrepPanel";
import IdentifyStreetParcels from "./DataPrep/IdentifyStreetParcels";
import LayerGroupPanel from "./DataPrep/LayerGroupPanel";
import ModificationsDraw from "./DataPrep/ModificationsDraw";
import { PathPreferencing } from "./DataPrep/PathPreferencing";
import ArtefactsPanel from "./ServiceArea/ArtefactsPanel";

const undergroundLayerIds = inputLayerGroups.inUnderground.layers.map(({ id }) => id);
const leadinLayerIds = inputLayerGroups.inLeadIns.layers.map(({ id }) => id);
const aerialLayerIds = inputLayerGroups.spanPole.layers.map(({ id }) => id);

export const extendedDesignStackedNavigationPanels: Array<{ id: string; name: string; component: React.ReactNode; useDefaultHeader?: boolean }> = [
  {
    id: "addresses",
    name: "Addresses",
    component: <InputMethodsPanel layerGroupId={LayerGroupIds.inAddress} />,
  },
  {
    id: "parcels",
    name: "Parcels",
    component: <InputMethodsPanel layerGroupId={LayerGroupIds.inParcel} />,
  },
  {
    id: "baseDataStreet",
    name: "Streets",
    component: <InputMethodsPanel layerGroupId={LayerGroupIds.inBaseDataStreet} />,
  },
  {
    id: "extended-design-aerial",
    name: "Aerial span and poles",
    component: (
      <LayerGroupPanel
        title="Aerial span and poles"
        navigationKey="extended-design-aerial"
        layerIds={aerialLayerIds}
        inputLayerGroupId={LayerGroupIds.spanPole}
      />
    ),
    useDefaultHeader: false,
  },
  {
    id: "extended-design-aerial-draw",
    name: "Edit aerial",
    component: <ModificationsDraw layerIds={aerialLayerIds} title="Edit aerial" />,
    useDefaultHeader: false,
  },
  {
    id: "centralOffice",
    name: "Central Office",
    component: <InputMethodsPanel layerGroupId={LayerGroupIds.inExchange} />,
  },
  {
    id: "area-select",
    name: "Area Select",
    component: <AreaSelect />,
    useDefaultHeader: false,
  },
  {
    id: "upload",
    name: "Upload",
    component: <UploadPanel />,
    useDefaultHeader: false,
  },
  {
    id: "draw",
    name: "Draw on Map",
    component: <Draw />,
    useDefaultHeader: false,
  },
  {
    id: "draw-polygon",
    name: "Draw Polygon",
    component: <PolygonSelect />,
    useDefaultHeader: false,
  },
  {
    id: "import-data",
    name: "Import data",
    component: <ImportData />,
  },
  {
    id: "use-existing-polygon",
    name: "Use existing polygon",
    component: <PolygonSelect />,
    useDefaultHeader: false,
  },
  {
    id: "extended-design-modifications",
    name: "Modify data",
    component: (
      <LayerPanel title="Modify data" layerKey="extended-design-modifications">
        <LayerDrawAction subtitle="Edit or draw data on the map" />
        <LayerUploadAction />
        <LayerPathPreferencesAction />
      </LayerPanel>
    ),
    useDefaultHeader: false,
  },
  {
    id: "extended-design-modifications-draw",
    name: "Modify data",
    component: <ModificationsDraw />,
    useDefaultHeader: false,
  },
  {
    id: "extended-design-modifications-upload",
    name: "Upload Base Data",
    component: (
      <MultiLayerUploadForm
        title="Upload Base Data"
        sections={[
          { layerId: LayerIds.inAddress, displayName: "Address", geometryType: "Point" },
          { layerId: LayerIds.inStreet, displayName: "Underground path", geometryType: "LineString" },
          { layerId: LayerIds.inStreetPoints, displayName: "Underground points", geometryType: "Point" },
          { layerId: LayerIds.inLeadIns, displayName: "Leadins", geometryType: "LineString" },
        ]}
      />
    ),
    useDefaultHeader: false,
  },
  {
    id: "extended-design-modifications-path-preferences",
    name: "Path Preferences",
    component: <PathPreferencing />,
    useDefaultHeader: false,
  },
  {
    id: "extended-design-underground",
    name: "Underground path and points",
    component: <LayerGroupPanel title="Underground path and points" navigationKey="extended-design-underground" layerIds={undergroundLayerIds} />,
    useDefaultHeader: false,
  },
  {
    id: "extended-design-underground-draw",
    name: "Edit underground",
    component: <ModificationsDraw layerIds={undergroundLayerIds} title="Edit underground" />,
    useDefaultHeader: false,
  },
  {
    id: "extended-design-underground-upload",
    name: "Upload underground",
    component: (
      <MultiLayerUploadForm
        title="Upload underground"
        sections={[
          { layerId: LayerIds.inStreet, displayName: "Underground path", geometryType: "LineString" },
          { layerId: LayerIds.inStreetPoints, displayName: "Underground points", geometryType: "Point" },
        ]}
      />
    ),
    useDefaultHeader: false,
  },
  {
    id: "extended-design-leadins",
    name: "Leadins",
    component: <LayerGroupPanel title="Leadins" navigationKey="extended-design-leadins" layerIds={leadinLayerIds} />,
    useDefaultHeader: false,
  },
  {
    id: "extended-design-leadins-draw",
    name: "Edit leadins",
    component: <ModificationsDraw layerIds={leadinLayerIds} title="Edit leadins" />,
    useDefaultHeader: false,
  },
  {
    id: "extended-design-leadins-upload",
    name: "Upload leadins",
    component: (
      <MultiLayerUploadForm title="Upload leadins" sections={[{ layerId: LayerIds.inLeadIns, displayName: "Leadins", geometryType: "LineString" }]} />
    ),
    useDefaultHeader: false,
  },
  {
    id: "extended-design-blockers",
    name: "Blockers",
    component: <BlockersRefinementPanel />,
    useDefaultHeader: false,
  },
  {
    id: "extended-design-blockers-draw",
    name: "Edit blockers",
    component: <BlockersDraw />,
    useDefaultHeader: false,
  },
  {
    id: "extended-design-blockers-upload",
    name: "Upload blockers",
    component: (
      <MultiLayerUploadForm title="Upload blockers" sections={[{ layerId: LayerIds.blocker, displayName: "Blockers", geometryType: "LineString" }]} />
    ),
    useDefaultHeader: false,
  },
  {
    id: "identify-street-parcels",
    name: "Review problematic parcels",
    component: <IdentifyStreetParcels />,
  },
  {
    id: "construction-prints-export",
    name: "Export",
    component: <ConstructionPrintExport />,
    useDefaultHeader: false,
  },
  {
    id: "remove-data",
    name: "Remove Data",
    component: <RemoveData />,
    useDefaultHeader: false,
  },
  {
    id: "design-hubs",
    name: "Hubs",
    component: <HubsRefinementPanel />,
    useDefaultHeader: false,
  },
  {
    id: "design-drop-assignments",
    name: "Drop assignments",
    component: <DropAssignmentsRefinementPanel />,
    useDefaultHeader: false,
  },
];

interface ExtendedDesignPanelProps {
  /**
   * The current architecture.
   */
  architecture: Architecture | null;
  /**
   * The project currently being viewed / edited
   */
  project: Project;
  /**
   * Flag indicating if the project is read only
   */
  readOnly: boolean;
}

const ExtendedDesignPanel: React.FC<ExtendedDesignPanelProps> = ({ architecture, project, readOnly }: ExtendedDesignPanelProps) => {
  const dispatch = useAppDispatch();
  const modal = useAppSelector((state) => state.project.modal);
  const architectureModalTabId = useAppSelector((state) => state.project.architectureModalTabId);
  const versionId = useAppSelector((state) => state.project.versionId);
  const { layerKeys } = useGetPlannerLayers(versionId);
  const { hasRequiredInputLayers, isDataPrepComplete } = usePlannerDataPrepStatus(versionId);
  const isInputDataComplete = hasRequiredInputLayers && isDataPrepComplete;
  const { data: versionStatus } = useGetVersionStatusQuery(versionId, { skip: !versionId });
  const { workflowState: dataPrepWorkflowState } = useCustomWorkflow({ versionId, key: WORKFLOW_KEYS.PLANNER_EXTENDED.DATA_PREP });

  useGetAccountReports();
  const versionReports = useAppSelector((store) => selectReportsByVersionId(store, versionId));

  const handleOnArchitectureModalClose = () => dispatch(closeArchitectureModal());
  const handleOnChangeArchitectureClick = useCallback(() => dispatch(openArchitectureModal()), [dispatch]);

  const accordionPanes: AccordionStepperPane[] = useMemo(
    () => [
      {
        header: "Architecture",
        id: "architecture",
        content: (
          <div style={{ textAlign: "left" }}>
            <ArchitecturePanel architecture={architecture} readOnly={readOnly} onChangeClick={handleOnChangeArchitectureClick} />
          </div>
        ),
        isComplete: architecture !== null,
      },
      {
        header: "Input data",
        id: "inputData",
        content: <DataPrepPanel project={project} versionId={versionId} readOnly={readOnly} />,
        isComplete: isInputDataComplete,
        isLoading: dataPrepWorkflowState === "STARTING" || dataPrepWorkflowState === "IN_PROGRESS",
        autoMove: false,
      },
      {
        header: "Design",
        id: "design",
        content: <SolvePanel />,
        isComplete: versionStatus?.Status === StatusTypes.Complete && !versionStatus.IsDirty,
      },
      {
        header: "Artifacts",
        id: "artifacts",
        content: <ArtefactsPanel project={project} versionId={versionId} architecture={architecture} readOnly={readOnly} />,
        isComplete: layerKeys.includes(LayerIds.serviceArea),
        optional: true,
      },
      {
        header: "Reports",
        id: "reports",
        content: <ReportPanel reports={versionReports} project={project} versionId={versionId} readOnly={readOnly} />,
        isComplete: versionReports.length > 0,
        optional: true,
      },
    ],
    [
      architecture,
      readOnly,
      handleOnChangeArchitectureClick,
      isInputDataComplete,
      layerKeys,
      project,
      versionId,
      versionStatus?.Status,
      versionStatus?.IsDirty,
      dataPrepWorkflowState,
      versionReports,
    ]
  );

  if (!project) return null;

  return (
    <Box data-testid="planner-extended-design-panel" sx={{ height: "100%" }}>
      {!permissionCheck(project.Permission?.Level, Actions.PROJECT_EDIT) ? ( // if user has access and no license, more granular "locks" are applied per component
        <NonIdealState size="small" icon={<Lock />} title="View only access" description="You only have view access to this project" />
      ) : (
        <StackedNavigation
          rootComponent={<AccordionStepper panes={accordionPanes} id={project.ID} />}
          screens={extendedDesignStackedNavigationPanels}
        />
      )}
      {modal === Modals.architecture && (
        <ArchitectureModal
          onClose={() => handleOnArchitectureModalClose()}
          project={project}
          architecture={architecture}
          initialTabId={architectureModalTabId ?? undefined}
        />
      )}
    </Box>
  );
};

export default ExtendedDesignPanel;
